import { useSelector } from '@xstate/react';

import Login from 'components/Login';
import Message, { mapSeverityToVariant } from 'components/Message';
import Skiplink from 'components/Skiplink';
import {
	HEADER_SEARCH_FIELD_ID,
	HEADER_SEARCH_FIELD_MOBILE_ID,
	MAIN_CONTENT_CONTAINER_ID,
} from 'constants/ids';
import {
	useCartContext,
	useGlobalMachinesContext,
	useGlobalMemberPopoversContext,
	useWishlistContext,
} from 'contexts';
import { useDidMount, useSafeFieldValues } from 'hooks';
import type { PageHeaderFields, SitecoreBlockProps } from 'models/sitecore';
import {
	selectIsAuthenticatedUser,
	selectIsInitialisingUser,
} from 'state-machines/session';

import PageHeader from './PageHeader';

interface Props extends SitecoreBlockProps {
	fields?: PageHeaderFields;
}

export default function PageHeaderBlock({ fields }: Props) {
	const { sessionService } = useGlobalMachinesContext();
	const { openLogin, openMenu } = useGlobalMemberPopoversContext();
	const { quantity: cartQuantity } = useCartContext();
	const { quantity: wishlistQuantity } = useWishlistContext();

	const texts = useSafeFieldValues(fields);
	const didMount = useDidMount();

	// Auth
	// Check mounted state to default to true for both server and client. The
	// selector returns true on the server since the machine starts there (even
	// though it probably shouldn't) and can sometimes return false on the
	// initial client render for some reason, leading to hydration error.
	// (Warning: Prop `aria-label` did not match. Server: "Laddar" Client: "Logga in")
	// TODO: Do something with the machine initialization to fix this properly.
	const isInitialisingUser =
		useSelector(sessionService, selectIsInitialisingUser) || !didMount;

	// Auth user
	const isAuthenticatedUser = useSelector(
		sessionService,
		selectIsAuthenticatedUser,
	);

	const handleLoginClick = () => {
		openLogin();
	};

	const handleAccountClick = () => {
		openMenu();
	};

	if (!fields) {
		return null;
	}

	return (
		<>
			{fields.systemMessage?.activated && (
				<Message
					className="justify-center print:hidden"
					variant={mapSeverityToVariant(fields.systemMessage.severity)}
					heading={fields.systemMessage.heading}
					text={fields.systemMessage.message}
				/>
			)}
			<Skiplink
				target={MAIN_CONTENT_CONTAINER_ID}
				text={texts.skipLinkToContentText}
				underline
			/>
			<Skiplink
				target={HEADER_SEARCH_FIELD_ID}
				text={texts.skipLinkToSearchText}
				className="max-headerRow:hidden"
				scroll={false}
				underline
			/>
			<Skiplink
				target={HEADER_SEARCH_FIELD_MOBILE_ID}
				text={texts.skipLinkToSearchText}
				className="headerRow:hidden"
				scroll={false}
				underline
			/>
			<PageHeader
				accountMenuButtonLabel={texts.accountMenuButtonLabel}
				cartQuantity={cartQuantity}
				primaryMenuLabel={texts.primaryMenuLabel}
				topBarMenuLabel={texts.topBarMenuLabel}
				inspirationMediaHeading={texts.inspirationMediaHeading}
				inspirationLinksHeading={texts.inspirationLinksHeading}
				isInitialisingUser={isInitialisingUser}
				isAuthenticatedUser={isAuthenticatedUser}
				mainMenu={fields.mainMenu}
				mobileMenuCloseText={texts.mobileMenuCloseText}
				navigationCategoryShowAllButton={texts.navigationCategoryShowAllButton}
				onAccountClick={handleAccountClick}
				onLoginClick={handleLoginClick}
				primaryTopLinks={fields.primaryTopLinks}
				review={fields.review}
				mobileMenuButtonLabel={texts.mobileMenuButtonLabel}
				searchBrandSuggestionLabel={texts.searchBrandSuggestionLabel}
				searchCancelButtonText={texts.searchCancelButtonText}
				searchCategorySuggestionLabel={texts.searchCategorySuggestionLabel}
				searchFieldPlaceholderText={texts.searchFieldPlaceholderText}
				searchInspirationSuggestionLabel={
					texts.searchInspirationSuggestionLabel
				}
				searchOpenSearchButtonLabel={texts.searchOpenSearchButtonLabel}
				searchOtherSearchTermRecommendationsText={
					texts.searchOtherSearchTermRecommendationsText
				}
				popularProductsText={texts.popularProductsText}
				popularSearchTermsText={texts.popularSearchTermsText}
				searchResultProductsText={texts.searchResultProductsText}
				searchResultsText={texts.searchResultsText}
				searchResultsSpellingSuggestionText={
					texts.searchResultsSpellingSuggestionText
				}
				searchFieldLabel={texts.searchFieldLabel}
				searchNoResultsText={texts.searchNoResultsText}
				searchStoreSuggestionLabel={texts.searchStoreSuggestionLabel}
				searchSuggestionLabel={texts.searchSuggestionLabel}
				secondaryTopLinks={fields.secondaryTopLinks}
				storePopoverChangeStoreTitle={texts.storePopoverChangeStoreTitle}
				storePopoverSelectStoreTitle={texts.storePopoverSelectStoreTitle}
				uspLinks={fields.uspLinks}
				wishlistQuantity={wishlistQuantity}
				wishlistButtonLabel={texts.wishlistButtonLabel}
				cartButtonLabel={texts.cartButtonLabel}
			/>
			<Login />
		</>
	);
}
PageHeaderBlock.displayName = 'PageHeaderBlock';
